import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { authService } from '../services/authService';
import type { User } from '../types/user';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  signIn: (params: { email: string; password: string }) => Promise<any>;
  signUp: (email: string, password: string, name: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      // Check local storage developer bypass first
      const savedUser = localStorage.getItem('demo_user');
      if (savedUser) {
        setUser(JSON.parse(savedUser));
        setLoading(false);
        return;
      }

      // Always use real AWS Cognito authentication
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.error('Check user error:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async ({ email, password }: { email: string; password: string }) => {
    // Check for developer bypass credentials
    if (email === 'student@cloudbinary.io' && password === 'demo123') {
      const mockUser = {
        id: 'mock-student-id-123',
        email: 'student@cloudbinary.io',
        name: 'Demo Student',
        role: 'student',
        groups: ['Student'],
        avatar: undefined,
        createdAt: new Date().toISOString()
      };
      localStorage.setItem('demo_user', JSON.stringify(mockUser));
      setUser(mockUser as any);
      return { success: true, isSignedIn: true };
    }

    if (email === 'admin@cloudbinary.io' && password === 'admin123') {
      const mockUser = {
        id: 'mock-admin-id-123',
        email: 'admin@cloudbinary.io',
        name: 'Demo Admin',
        role: 'admin',
        groups: ['Admin'],
        avatar: undefined,
        createdAt: new Date().toISOString()
      };
      localStorage.setItem('demo_user', JSON.stringify(mockUser));
      setUser(mockUser as any);
      return { success: true, isSignedIn: true };
    }

    try {
      // Sign out any existing session first
      try {
        await authService.signOut();
      } catch (e) {
        // Ignore if no existing session
      }
      
      // Now sign in with new credentials
      const result = await authService.signIn({ email, password });

      // Generate a new session ID and save to local storage
      const sessionId = Math.random().toString(36).substring(2) + Date.now().toString(36);
      localStorage.setItem('user_session_id', sessionId);
      
      // Update nickname attribute in Cognito to this session ID
      try {
        await authService.updateUserSession(sessionId);
      } catch (attrError) {
        console.warn('Failed to update session ID in Cognito:', attrError);
      }

      await checkUser();
      return result;
    } catch (error: any) {
      console.error('useAuth signIn error:', error);
      throw new Error(error.message || 'Failed to sign in');
    }
  };

  const signUp = async (email: string, password: string, name: string) => {
    try {
      await authService.signUp({ email, password, name });
      // Note: After sign up, user needs to verify email
      // You might want to redirect to a verification page
    } catch (error: any) {
      throw new Error(error.message || 'Failed to sign up');
    }
  };

  const signOut = async () => {
    localStorage.removeItem('demo_user');
    localStorage.removeItem('user_session_id');
    try {
      // Use real AWS Cognito sign out
      await authService.signOut();
      setUser(null);
    } catch (error: any) {
      setUser(null);
      throw new Error(error.message || 'Failed to sign out');
    }
  };

  const refreshUser = async () => {
    await checkUser();
  };

  useEffect(() => {
    if (!user) return;

    const checkSessionInterval = setInterval(async () => {
      try {
        // If developer mock/demo user bypass is active, skip check
        if (localStorage.getItem('demo_user')) return;

        const attributes = await authService.getUserAttributes();
        const activeSessionId = attributes.nickname;
        const localSessionId = localStorage.getItem('user_session_id');

        if (activeSessionId && localSessionId && activeSessionId !== localSessionId) {
          console.warn('Session mismatch detected! Logging out...');
          clearInterval(checkSessionInterval);
          
          // Clear authentication tokens and local user data
          localStorage.removeItem('demo_user');
          localStorage.removeItem('user_session_id');
          setUser(null);
          
          try {
            await authService.signOut();
          } catch (e) {
            // Ignore error if already signed out
          }
          
          // Redirect to login page with reason parameter
          window.location.href = '/login?reason=concurrent_session';
        }
      } catch (error) {
        console.error('Session verification check failed:', error);
      }
    }, 15000); // Check every 15 seconds for quick response

    return () => clearInterval(checkSessionInterval);
  }, [user]);

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    signIn,
    signUp,
    signOut,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
